/**
 * Shared notification types and interfaces
 * These types are used across the notification system for consistency
 */

/**
 * Interface representing user notification preferences for a specific notification type
 * @interface NotificationPreference
 */
export interface NotificationPreference {
  /** Unique identifier for the preference */
  uid?: string;
  /** Type of notification */
  type: NotificationType;
  /** Whether email notifications are enabled */
  email: boolean;
  /** Whether push notifications are enabled */
  push: boolean;
  /** Whether SMS notifications are enabled */
  sms: boolean;
  /** Whether this notification type is globally enabled */
  enabled: boolean;
}

/**
 * Interface representing comprehensive notification settings for a user
 * @interface UserNotificationSettings
 */
export interface UserNotificationSettings {
  /** Unique identifier for the settings record */
  uid?: string;
  /** User ID this setting belongs to */
  userId: string;
  /** User's email address for notifications */
  email: string;
  /** User's SMS number for notifications */
  smsNumber: string;
  /** Whether notifications are globally enabled for this user */
  globalEnabled?: boolean;
  /** Whether user wants weekly reports */
  weeklyReports: boolean;
  /** Whether user wants threat intelligence updates */
  threatIntelUpdates: boolean;
  /** Quiet hours configuration */
  quietHours: {
    /** Whether quiet hours are enabled */
    enabled: boolean;
    /** Start time for quiet hours (HH:MM format) */
    startTime: string;
    /** End time for quiet hours (HH:MM format) */
    endTime: string;
  };
  /** User's timezone */
  timezone: string;
  /** Whether to send notifications only on weekends */
  weekendsOnly: boolean;
  /** Array of notification preferences by type */
  preferences: NotificationPreference[];
  /** When the settings were created */
  createdAt?: Date;
  /** When the settings were last updated */
  updatedAt?: Date;
}

/**
 * Enumeration of available notification types in the system
 * @enum {string}
 */
export enum NotificationType {
  /** Security alerts for immediate threats */
  SECURITY_ALERTS = 'security-alerts',
  /** Critical vulnerability notifications */
  CRITICAL_VULNERABILITIES = 'critical-vulnerabilities',
  /** Scan completion notifications */
  SCAN_COMPLETION = 'scan-completion',
  /** Failed scan notifications */
  FAILED_SCANS = 'failed-scans',
  /** Compliance-related alerts */
  COMPLIANCE_ALERTS = 'compliance-alerts',
  /** Remediation status updates */
  REMEDIATION_UPDATES = 'remediation-updates',
  /** System maintenance notifications */
  SYSTEM_MAINTENANCE = 'system-maintenance',
  /** Account-related changes */
  ACCOUNT_CHANGES = 'account-changes',
  /** Platform update notifications */
  PLATFORM_UPDATES = 'platform-updates',
  /** Marketing and promotional messages */
  MARKETING_PROMOTIONS = 'marketing-promotions',
}

/**
 * Supported notification integration channel types.
 * @enum {string}
 */
export enum NotificationIntegrationChannelType {
  EMAIL = 'email',
  SLACK = 'slack',
  TEAMS = 'teams',
  PUSH = 'push',
}

/**
 * Per-channel configuration for outbound notification integrations.
 * Scoped to a user + company pair so members of multiple companies each get
 * independent channel settings per company context.
 * Stored in Firestore under `notification-integrations/{docId}` where the
 * document is looked up by (userId, companyId).
 * @interface NotificationIntegrationsConfig
 */
export interface NotificationIntegrationsConfig {
  /** Unique document identifier */
  uid?: string;
  /** Firebase user ID this config belongs to */
  userId: string;
  /** Company ID this config is scoped to */
  companyId: string;
  /** Email channel settings */
  email: {
    enabled: boolean;
    /** Up to 5 recipient addresses */
    recipients: string[];
  };
  /** Slack incoming-webhook settings */
  slack: {
    enabled: boolean;
    webhookUrl: string;
    /** Optional channel name shown in the UI only (e.g. "#security") */
    channel?: string;
  };
  /** Microsoft Teams incoming-webhook settings */
  teams: {
    enabled: boolean;
    webhookUrl: string;
  };
  /** In-app push via SignalR */
  push: {
    enabled: boolean;
  };
  updatedAt?: Date;
}

/**
 * Default notification integrations config for a new user.
 * @constant {Omit<NotificationIntegrationsConfig, 'uid' | 'userId' | 'updatedAt'>}
 */
export const DEFAULT_NOTIFICATION_INTEGRATIONS: Omit<
  NotificationIntegrationsConfig,
  'uid' | 'userId' | 'companyId' | 'updatedAt'
> = {
  email: { enabled: true, recipients: [] },
  slack: { enabled: false, webhookUrl: '', channel: '#security' },
  teams: { enabled: false, webhookUrl: '' },
  push: { enabled: true },
};

/**
 * Default notification preferences applied to new users
 * @constant {NotificationPreference[]}
 */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreference[] = [
  {
    type: NotificationType.SECURITY_ALERTS,
    email: true, // Required - cannot be disabled
    push: false, // Optional - can be enabled by user
    sms: false, // Optional - can be enabled by user
    enabled: true,
  },
  {
    type: NotificationType.CRITICAL_VULNERABILITIES,
    email: true,
    push: true,
    sms: false,
    enabled: true,
  },
  {
    type: NotificationType.SCAN_COMPLETION,
    email: true,
    push: false,
    sms: false,
    enabled: true,
  },
  {
    type: NotificationType.FAILED_SCANS,
    email: true,
    push: true,
    sms: false,
    enabled: true,
  },
  {
    type: NotificationType.COMPLIANCE_ALERTS,
    email: true,
    push: false,
    sms: true,
    enabled: true,
  },
  {
    type: NotificationType.REMEDIATION_UPDATES,
    email: true,
    push: false,
    sms: false,
    enabled: true,
  },
  {
    type: NotificationType.SYSTEM_MAINTENANCE,
    email: true,
    push: false,
    sms: false,
    enabled: true,
  },
  {
    type: NotificationType.ACCOUNT_CHANGES,
    email: true,
    push: false,
    sms: false,
    enabled: true,
  },
  {
    type: NotificationType.PLATFORM_UPDATES,
    email: true,
    push: false,
    sms: false,
    enabled: true,
  },
  {
    type: NotificationType.MARKETING_PROMOTIONS,
    email: false,
    push: false,
    sms: false,
    enabled: true,
  },
];
